import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { DataGrid, GridActionsCellItem, gridClasses } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router";
import { useDialogs } from "../hooks/useDialogs/useDialogs";
import useNotifications from "../hooks/useNotifications/useNotifications";
import {
  getAllJobs,
  deleteJobById,
  getJobs,
} from "../../services/jobService";
import PageContainer from "./PageContainer";
import { useAuth } from "../../auth/AuthContext";

const INITIAL_PAGE_SIZE = 10;

export default function JobList() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [allJobs, setAllJobs] = React.useState([]);
  const [filteredJobs, setFilteredJobs] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // search/filter inputs
  const [searchQuery, setSearchQuery] = React.useState("");
  const [locationFilter, setLocationFilter] = React.useState("");
  const [departmentFilter, setDepartmentFilter] = React.useState("");

  // dropdown options
  const [locations, setLocations] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);

  // ✅ Load jobs once
  const loadData = React.useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      let response;
      if (user?.role === "HR") {
        response = await getJobs({});
      } else {
        response = await getAllJobs({});
      }

      const rows = response.message || [];
      setAllJobs(rows);
      setFilteredJobs(rows);

      // extract unique locations and departments for dropdowns
      setLocations([...new Set(rows.map((job) => job.location).filter(Boolean))]);
      setDepartments([...new Set(rows.map((job) => job.department).filter(Boolean))]);
    } catch (err) {
      setError(err);
    }

    setIsLoading(false);
  }, [user?.role]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    loadData();
    setSearchQuery("");
    setLocationFilter("");
    setDepartmentFilter("");
  };

  // ✅ Apply client-side filters/search
  React.useEffect(() => {
    let result = [...allJobs];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (job) =>
          job.jobTitle?.toLowerCase().includes(q) ||
          job.department?.toLowerCase().includes(q) ||
          job.location?.toLowerCase().includes(q)
      );
    }

    if (locationFilter) {
      result = result.filter((job) => job.location === locationFilter);
    }

    if (departmentFilter) {
      result = result.filter((job) => job.department === departmentFilter);
    }

    setFilteredJobs(result);
  }, [searchQuery, locationFilter, departmentFilter, allJobs]);

  const handleRowClick = ({ row }) => {
    navigate(`/app/job/${row.id}`);
  };

  const handleCreateClick = () => {
    navigate("/app/hr/jobs/new");
  };

  const handleRowEdit = (job) => () => {
    navigate(`/app/hr/job/${job.id}/edit`);
  };

  const handleRowDelete = (job) => async () => {
    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${job.jobTitle}?`,
      {
        title: `Delete job?`,
        severity: "error",
        okText: "Delete",
        cancelText: "Cancel",
      }
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        await deleteJobById(Number(job.id));

        notifications.show("Job deleted successfully.", {
          severity: "success",
          autoHideDuration: 3000,
        });
        loadData();
      } catch (deleteError) {
        notifications.show(
          `Failed to delete job. Reason: ${deleteError.message}`,
          {
            severity: "error",
            autoHideDuration: 3000,
          }
        );
      }
      setIsLoading(false);
    }
  };

  // ✅ Only the requested 5 columns
  const columns = [
    { field: "jobTitle", headerName: "Title", flex: 1 },
    { field: "department", headerName: "Department", width: 160 },
    { field: "location", headerName: "Location", width: 180 },
    { field: "employmentType", headerName: "Employment Type", width: 160 },
    { field: "experienceLevel", headerName: "Experience", width: 140 },
    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: ({ row }) => {
        if (user?.role === "HR") {
          if (row.userId === user?.id) {
            return [
              <GridActionsCellItem
                key="edit-item"
                icon={<EditIcon />}
                label="Edit"
                onClick={handleRowEdit(row)}
              />,
              <GridActionsCellItem
                key="delete-item"
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleRowDelete(row)}
              />,
            ];
          }
          return [];
        } else {
          return [
            <GridActionsCellItem
              key="apply-item"
              icon={<AddIcon />}
              label="Apply"
              onClick={() => {
                notifications.show("Applied successfully!", {
                  severity: "success",
                });
              }}
            />,
          ];
        }
      },
    },
  ];

  const pageTitle = "Job Listings";

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleRefresh}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          {user?.role === "HR" && (
            <Button
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
            >
              Create
            </Button>
          )}
        </Stack>
      }
    >
      {/* 🔎 Search + Filters */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small"
          label="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
          }}
        />
        <TextField
          select
          size="small"
          label="Filter by Location"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc} value={loc}>
              {loc}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Filter by Department"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          {departments.map((dep) => (
            <MenuItem key={dep} value={dep}>
              {dep}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ flex: 1, width: "100%" }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <DataGrid
            rows={filteredJobs}
            rowCount={filteredJobs.length}
            columns={columns}
            pagination
            pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
            }}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            loading={isLoading}
            sx={{
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: "transparent",
              },
              [`& .${gridClasses.row}:hover`]: {
                cursor: "pointer",
              },
            }}
          />
        )}
      </Box>
    </PageContainer>
  );
}
